import { useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ConsentField from '../../components/ConsentField/ConsentField';
import Description from '../../components/Description/Description';
import { ErrorMessage } from '../../components/ErrorMessage';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import Subtitle from '../../components/Subtitle/Subtitle';
import TextInputField from '../../components/TextInputField/TextInputField';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { ConsumerLoanTargetPath } from '../../domain/types';
import { EventApplicationSubmitted } from '../../helpers/gtm';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { getPath } from '../../helpers/url';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import useGetConsentService from '../../hooks/useGetConsentService';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useSetRightPanelContent from '../../hooks/useSetRightPanelContent';
import useSettings from '../../hooks/useSettings';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { submitApplication } from '../../store/application/application';
import { addOrUpdateFormField, invalidateForm } from '../../store/applicationform';
import { selectFieldValue } from '../../store/applicationform/applicationFormSelectors';
import { selectGovernmentIdPageIndex, setCurrentPage, WizardPageAction } from '../../store/wizard/wizard';
import { AttributeNames } from '../../types/AttributeNames';
import SubmitPageSidePanelDK from './SubmitPageSidePanelDK';

// Fallback wizard index of EskatKreditStatusConsentPageDK (the government-id page) in the DK main-applicant
// wizard. Only used if that page never recorded its actual position (it always does before Submit is
// reachable). See selectGovernmentIdPageIndex / setGovernmentIdPageIndex (LP-2980, mirrors the generic
// SubmitPage's VERIFY_INCOME_PAGE_FALLBACK_INDEX from LP-2699). Exported so tests assert against the same
// value rather than duplicating the literal.
export const ESKAT_KREDITSTATUS_PAGE_FALLBACK_INDEX = 1;

const SubmitPage = () => {
    useComponentTracker('SubmitPage');
    const translate = useTranslation();
    const theme = useTheme();
    const greaterThanLg = useMediaQuery(theme.breakpoints.up('lg'));
    const { totalSections, totalAboutTheLoanPages } = usePagesConfig();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { currentPage } = usePagesContext();
    const { languageCode, countryCode } = useSettings();
    const applicationId = useAppSelector((state) => state.application.applicationId);
    const [errorOccurred, setErrorOccurred] = useState<boolean>(false);
    const { consentDetails, isError } = useGetConsentService('TERMS_LOANS');
    const defaultPagesAction = useDefaultPagesAction();
    const coApplicantGovernmentId = useAppSelector(selectFieldValue)(AttributeNames.coapplicantGovernmentId);
    const governmentIdPageIndex = useAppSelector(selectGovernmentIdPageIndex);
    const pagesActions: WizardPageAction[] = coApplicantGovernmentId ? ['continue-with-coapplicant'] : ['submit'];

    useSetBannerType('none');

    const sidePanelContent = useMemo(() => <SubmitPageSidePanelDK />, []);
    useSetRightPanelContent(greaterThanLg ? sidePanelContent : null);

    const requiredFieldConsents =
        consentDetails
            ?.filter((consentDetails) => consentDetails.required)
            .map((consentDetails) => consentDetails.name) ?? [];

    // Validate the required consents plus applicantGovernmentId (collected earlier, on
    // EskatKreditStatusConsentPageDK) -- matches the generic SubmitPage's LP-2699 gate. Without this a rare
    // failed MitID extraction (LP-2980) could reach the backend with no government id and get declined, with
    // no way for the applicant to have fixed it even if the app had told them.
    const fieldsToValidate = [...requiredFieldConsents, AttributeNames.applicantGovernmentId];
    const validationErrors = useValidation(fieldsToValidate);

    useEffect(() => {
        setErrorOccurred(false);
    }, []);

    const onAction = async (action: WizardPageAction) => {
        if (action === 'submit' || action === 'continue-with-coapplicant') {
            if (validationErrors.length) {
                const governmentIdMissing = validationErrors.some(
                    (validationError) => validationError.fieldName === AttributeNames.applicantGovernmentId,
                );
                // Persist every other error so the affected fields render their error state. Deliberately
                // excluding the government-id one: unlike NO/SE's VerifyIncomePageNO, there is no rendered
                // input for it here to show an error on, and EskatKreditStatusConsentPageDK's own extraction
                // effect only ever dispatches `value` (never `hasError`) -- so persisting a synthetic
                // hasError: true for it would survive a later successful MitID retry untouched
                // (addUpdateFormFieldArray only overwrites keys present in the new payload) and permanently
                // re-trigger this same routing-back branch on every future submit attempt.
                validationErrors
                    .filter((validationError) => validationError.fieldName !== AttributeNames.applicantGovernmentId)
                    .forEach((validationError) => {
                        dispatch(addOrUpdateFormField(validationError));
                    });

                if (governmentIdMissing) {
                    // The government-id field is collected on EskatKreditStatusConsentPageDK, not on this
                    // page (and has no other source for the main applicant, unlike NO/SE's manually-typed
                    // fallback). Rather than silently submitting an empty value, route the applicant back to
                    // retry the MitID login, using the index that page recorded for the active wizard.
                    dispatch(setCurrentPage(governmentIdPageIndex ?? ESKAT_KREDITSTATUS_PAGE_FALLBACK_INDEX));
                } else {
                    scrollToFieldWithError(validationErrors);
                }
                return false;
            }
            try {
                const response = await dispatch(submitApplication()).unwrap();
                await dispatch(invalidateForm());
                navigate(
                    getPath(
                        ConsumerLoanTargetPath.ongoing,
                        languageCode,
                        countryCode,
                        response.applicationReferenceNumber,
                    ),
                );
            } catch (error) {
                setErrorOccurred(true);
                throw error;
            } finally {
                EventApplicationSubmitted(applicationId, countryCode, 'main');
            }
            return false;
        } else {
            return defaultPagesAction(action);
        }
    };

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={onAction}
                currentPage={currentPage}
                currentSection={1}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutTheLoanPages}
                currentPageInSection={2}
                sectionTitle={translate('field.section.about-the-loan')}
            />
            <Title text={translate('page.submit.title')} />
            <Description text={translate('page.submit.description')} />

            {!greaterThanLg && <SubmitPageSidePanelDK />}

            <Subtitle text={translate('page.submit.subtitle.consent')} />
            {consentDetails?.map((consentDetail) => (
                <ConsentField key={consentDetail.name} consentDetail={consentDetail} />
            ))}
            <TextInputField name="experianBudgetId" label={translate('field.experian-budget-id.label')} />
            {errorOccurred && (
                <ErrorMessage
                    title={translate(`page.submit.error.title`)}
                    description={translate(`page.submit.error.description`)}
                />
            )}
            {isError && (
                <ErrorMessage title={translate('page.error.title')} description={translate('page.error.description')} />
            )}
            <PagesFooter
                actions={pagesActions}
                onAction={onAction}
                currentSection={1}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutTheLoanPages}
                currentPageInSection={2}
                sectionTitle={translate('field.section.about-the-loan')}
            />
        </>
    );
};

export default SubmitPage;
